import { useMsal } from "@azure/msal-react";
import * as React from "react";
import { AddCircle20Regular } from "@fluentui/react-icons";
import { ComponentsHelper } from "../../core/services/ComponentHelper";
import { ILookupDictionary } from "../../lookup/types/ILookupDictionary";
import { setDataSource } from "../../redux/reducers/MembersPortalReducer";
import { FormatFields } from "../../redux/types/FormatFields";
import { IActiveAccordion } from "../../redux/types/IActiveAccordion";
import IMembersPortalComponentDataItem from "../../redux/types/IMembersPortalComponentDataItem";
import { IResultsPerPage } from "../../redux/types/IResultsPerPage";
import { ITabReduxItem } from "../../redux/types/ITabReduxItem";
import { PERMISSION_MESSAGE } from "../../repertoire/Consts";
import ITooltip from "../../repertoire/components/Tooltip/IToolTip";
import { SearchRequests } from "../../repertoire/services/SearchRequests";
import { DataSource } from "../../repertoire/types/DataSource";
import { CONFIGURATION_PARAMETER_FIELD_FORMAT_KEY, WORK_MAINTENANCE_GROUP } from "../ConfigurationConsts";
import { IRegisteredAgreementSearchQuery } from "../types/IRegisteredAgreementSearchQuery";
import { IRegisteredAgreementSearchResult } from "../types/IRegisteredAgreementSearchResult";
import RegisteredAgreementsSearchView from "./RegisteredAgreementsSearchView";
import { MembersPortalService } from "../../redux/services/MembersPortalService";
import { MembersPortalRoles } from "../../redux/types/MembersPortalRoles";
import InfoToggle from "../sharedComponents/button/InfoToggle";
import { IMembersPortalPathInfo } from "../types/IMembersPortalPathInfo";

export interface IRegisteredAgreementsPageProps {
    registeredAgreementsPageData?: IMembersPortalComponentDataItem;
    searchViewData: IMembersPortalComponentDataItem;
    searchResultsTableData: IMembersPortalComponentDataItem;
    searchRegisteredAgreements?: (query: IRegisteredAgreementSearchQuery, lookupValues: ILookupDictionary) => void;
    registeredAgreementsSearchResults: IRegisteredAgreementSearchResult[];
    memberAgreementSearchResults?: IRegisteredAgreementSearchResult[];
    searchSuccessful: boolean;
    registeredAgreementsMaintenanceGeneralDataViewData?: IMembersPortalComponentDataItem;
    lookupValues: ILookupDictionary;
    loadMembersPortalFromPath: (pathInfo: IMembersPortalPathInfo, currentUserNameNumber: string, location: Location) => void;
    indexOfFirstResult?: number;
    indexOfLastResult?: number;
    resultsPerPage?: number;
    currentPage?: number;
    resetPagination: (repertoireSection: string) => void;
    updatePagination: (indexOfFirstResult: number, indexOfLastResult: number, resultsPerPage: number, currentPage: number, repertoireSection: string) => void;
    getRegisteredAgreementDetails: (dataSource: string, workID: number, lookupValues: ILookupDictionary, registeredAgreementsMaintenanceDataViewData: IMembersPortalComponentDataItem, formats?: FormatFields[]) => void;
    addNewRegisteredAgreement: (lookups: ILookupDictionary, currentUserNameNumber?: string, isNew?: boolean, registeredAgreementsMaintenanceGeneralDataViewData?: IMembersPortalComponentDataItem, formats?: FormatFields[]) => void;
    sortSearchResults: (name: string, results: any) => void;
    defaultActiveAccordions?: IActiveAccordion[];
    allResultsPerPage?: IResultsPerPage[];
    updateUserPreferences: (allResultsPerPage: IResultsPerPage[], activeAccordions: IActiveAccordion[], newActiveAccordionName?: string, accordionExpanded?: boolean, componentName?: string, indexOfFirstResult?: number, indexOfLastResult?: number, resultsPerPage?: number, repertoireSection?: string) => void;
    disabled?: boolean;
    roles?: string[];
    saveRoles?: (roles: string[]) => void;
    setDataSource: typeof setDataSource;
    dataSource: string;
    activeTab?: number;
    tabs?: ITabReduxItem[];
    currentUserNameNumber?: string;
    expandWorkResult?: (workResult: number) => void;
    expandedWorkResults?: number[];
    expandAllResults: () => void;
    expandAll: boolean;
    registeredAgreementSearchResults?: any;
    getDraftAgreementDetails: (dataSource: string, agreementID: number, lookupValues: ILookupDictionary, registeredAgreementsMaintenanceGeneralDataViewData: IMembersPortalComponentDataItem, formats?: FormatFields[]) => void;
}

const RegisteredAgreementsPage: React.FC<IRegisteredAgreementsPageProps> = ({
    registeredAgreementsPageData,
    addNewRegisteredAgreement,
    lookupValues,
    registeredAgreementsMaintenanceGeneralDataViewData,
    currentUserNameNumber,
    searchViewData,
    searchResultsTableData,
    searchRegisteredAgreements,
    searchSuccessful,
    loadMembersPortalFromPath,
    indexOfFirstResult,
    indexOfLastResult,
    currentPage,
    resultsPerPage,
    resetPagination,
    updatePagination,
    sortSearchResults,
    expandAllResults,
    expandAll,
    registeredAgreementSearchResults,
    getDraftAgreementDetails,
    tabs,
    activeTab,
}) => {
    const [loaded, setLoaded] = React.useState(false);
    const { instance, accounts } = useMsal();
    React.useEffect(() => {
        checkIfLoaded();
        const account = accounts[0]
        ComponentsHelper.createBearerHeaderFromAADLogin(instance, account);
    }, [])
    React.useEffect(() => {
        checkIfLoaded();
    }, [registeredAgreementsPageData, loaded])
    const checkIfLoaded = () => {
        if (registeredAgreementsPageData.fields && registeredAgreementsPageData.fields.length >= 1 && !loaded) {
            setLoaded(true);
            const header = registeredAgreementsPageData.fields.find(f => f.name === "PageTitle");
            if (header) {
                document.title = header.data;
            }
        }
    }
    const addNewDraftAgreements = (): void => {
        SearchRequests.getConfigurationParameter(CONFIGURATION_PARAMETER_FIELD_FORMAT_KEY, WORK_MAINTENANCE_GROUP)
            .then(formats => {
                addNewRegisteredAgreement(lookupValues, currentUserNameNumber, true, registeredAgreementsMaintenanceGeneralDataViewData, formats);
            });
    }
    let currentDataSource = DataSource.Repertoire;
    const infoToggleHeaderField = registeredAgreementsPageData.fields
        .find(x => x.name === 'infoToggleHeader');

    const infoToggleHtmlField = registeredAgreementsPageData.fields
        .find(x => x.name === 'infoToggleHTML');


    const renderInfoToggle = (
        <InfoToggle
            headerTextItem={infoToggleHeaderField}
            htmlTextItem={infoToggleHtmlField}
            currentUserNameNumber={currentUserNameNumber}
            lookupValues={lookupValues}
            loadMembersPortalFromPath={loadMembersPortalFromPath}
        />
    )
    if (loaded) {
        const getAgreementDetails = (dataSource: string, agreementID: number) => {
            getDraftAgreementDetails(dataSource, agreementID, lookupValues, registeredAgreementsMaintenanceGeneralDataViewData);
        }
        let searchPageLabelTitle = null;
        if (registeredAgreementsPageData && registeredAgreementsPageData.fields.length > 0) {
            searchPageLabelTitle = registeredAgreementsPageData.fields.find((v, i, a) => v.name == "searchPageLabelTitle").data;
        }
        const hasPermission = MembersPortalService.hasPermissionForMenu("registerNewAgreement", MembersPortalRoles.FullAccess);
        let searchResults = registeredAgreementSearchResults;
        return (
            <div>
                <div className="row">
                    <div className="col-md-12">
                        <span className="title">{searchPageLabelTitle}</span>
                        {hasPermission ? (
                            <div className="newWorkButton" title="Register New Agreement">
                                <div className="IconTextButton" onClick={addNewDraftAgreements}>
                                    <AddCircle20Regular
                                        className="icon"
                                        aria-hidden="true"
                                    />
                                    { registeredAgreementsPageData.fields.find((v, i, a) => v.name == "registerNewAgreement").data }
                                </div>
                            </div>
                        ) : (
                            <div className="newWorkButton" title="Register New Agreement">
                                <ITooltip
                                    tooltipContent={PERMISSION_MESSAGE}
                                    showTooltip={true}
                                    placement="top"
                                >
                                    <span style={{ display: 'inline-block' }}>
                                        <div className="IconTextButton disabled">
                                            <AddCircle20Regular
                                                className="icon disabled"
                                                aria-hidden="true"
                                            />
                                            { registeredAgreementsPageData.fields.find((v, i, a) => v.name == "registerNewAgreement").data }
                                        </div>
                                    </span>
                                </ITooltip>
                            </div>
                        )}
                    </div>
                </div>
                {renderInfoToggle }
                <RegisteredAgreementsSearchView
                    searchViewData={searchViewData}
                    searchResultsTableData={searchResultsTableData}
                    lookupValues={lookupValues}
                    searchRegisteredAgreements={searchRegisteredAgreements}
                    registeredAgreementsSearchResults={searchResults}
                    searchSuccessful={searchSuccessful}
                    indexOfFirstResult={indexOfFirstResult}
                    indexOfLastResult={indexOfLastResult}
                    resultsPerPage={resultsPerPage}
                    currentPage={currentPage}
                    resetPagination={resetPagination}
                    updatePagination={updatePagination}
                    sortSearchResults={sortSearchResults}
                    expandAllResults={expandAllResults}
                    expandAll={expandAll}
                    currentUserNameNumber={currentUserNameNumber}
                    getRegisteredAgreementDetails={getAgreementDetails}
                    dataSource={currentDataSource}
                    tabs={tabs}
                    activeTab={activeTab}
                />
            </div>
        );
    }
    return <div>Loading ...</div>
}
export default RegisteredAgreementsPage;